// External Dependencies
import React, { useState, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiUpload } from 'react-icons/fi'
import { IoArrowBackOutline, IoClose } from 'react-icons/io5'

// Components
import CustomToast from '../../components/Custom/CustomToast'
import PageHeader from '../../components/Custom/PageHeader/PageHeader'

// Hooks and Utils
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'

// Constants
const FILE_SIZE_LIMIT = 5 * 1024 * 1024 // 5MB in bytes
const ACCEPTED_FILE_TYPE = 'image/svg+xml'

const PatternAdd = () => {
  const navigate = useNavigate()
  const GoBack = useGoBack()
  const userPermissions = useUserStore((state) => state.user) || []

  const fileInputRef = useRef(null)

  // Memoized Options
  const categories = useMemo(
    () => [
      'Brick Bond',
      'Paving',
      'Parquetry',
      'Geometric',
      'Organic',
      'Random',
      'Roofing',
    ],
    [],
  )

  const memberships = useMemo(() => ['Free', 'Premium', 'Pro'], [])

  // State Management
  const [isUploading, setIsUploading] = useState(false)
  const [formData, setFormData] = useState({
    patternName: '',
    selectedCategory: '',
    selectedMembership: [],
    // height: '',          // Commented - auto-calculated by Python
    // width: '',           // Commented - auto-calculated by Python
    // maxTileWidth: '',    // Commented - auto-calculated by Python
    rowMultiple: '',     // Keep - User can set manually if needed
    columnMultiple: '',  // Keep - User can set manually if needed
    materialFile: null,
    materialPreview: null,
  })
  const [errors, setErrors] = useState({})

  // Form Field Change Handler
  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }))
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  // Membership Checkbox Change Handler
  const handleMembershipChange = (membership) => (e) => {
    setFormData((prev) => {
      const updatedMemberships = e.target.checked
        ? [...prev.selectedMembership, membership]
        : prev.selectedMembership.filter((m) => m !== membership)

      return {
        ...prev,
        selectedMembership: updatedMemberships,
      }
    })
    setErrors((prev) => ({ ...prev, selectedMembership: '' }))
  }

  // Form Validation
  const validateInputs = () => {
    const newErrors = {}
    const {
      patternName,
      selectedCategory,
      selectedMembership,
      // height,
      // width,
      // maxTileWidth,
      rowMultiple,
      columnMultiple,
      materialFile,
    } = formData

    if (!patternName.trim()) {
      newErrors.patternName = 'Pattern name is required'
    }
    if (!selectedCategory) {
      newErrors.selectedCategory = 'Please select a category'
    }
    if (!selectedMembership || selectedMembership.length === 0) {
      newErrors.selectedMembership = 'Please select at least one membership'
    }
    if (!materialFile) {
      newErrors.materialFile = 'Please upload a pattern file'
    }

    // Optional validation for rowMultiple and columnMultiple
    // Uncomment if you want to make them required
    // if (!rowMultiple || isNaN(rowMultiple) || rowMultiple <= 0) {
    //   newErrors.rowMultiple = 'Please enter a valid row multiple'
    // }
    // if (!columnMultiple || isNaN(columnMultiple) || columnMultiple <= 0) {
    //   newErrors.columnMultiple = 'Please enter a valid column multiple'
    // }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // File Upload Handler
  const handleImageChange = (event) => {
    const file = event.target.files?.[0]
    if (!file) {
      setErrors((prev) => ({
        ...prev,
        materialFile: 'Please select a valid file',
      }))
      return
    }

    if (file.type !== ACCEPTED_FILE_TYPE) {
      setErrors((prev) => ({
        ...prev,
        materialFile: 'Only SVG files are allowed',
      }))
      return
    }

    if (file.size > FILE_SIZE_LIMIT) {
      setErrors((prev) => ({
        ...prev,
        materialFile: 'File size must be less than 5MB',
      }))
      return
    }

    setFormData((prev) => ({
      ...prev,
      materialFile: file,
      materialPreview: URL.createObjectURL(file),
    }))
    setErrors((prev) => ({ ...prev, materialFile: '' }))
  }

  // File Delete Handler
  const handleDeleteImage = () => {
    setFormData((prev) => ({
      ...prev,
      materialFile: null,
      materialPreview: null,
    }))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Form Submission Handler
  const handleSubmit = async () => {
    if (!userPermissions.includes('pattern:create')) {
      CustomToast({
        message: 'You do not have permission to create patterns',
        type: 'error',
      })
      return
    }

    if (!validateInputs()) return

    setIsUploading(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('name', formData.patternName)
      formDataToSend.append('category', formData.selectedCategory)

      // Send each membership as a separate field with the same name 'membership'
      formData.selectedMembership.forEach((membership) => {
        formDataToSend.append('membership', membership)
      })

      formDataToSend.append('file', formData.materialFile)
      formDataToSend.append('type', 'Pattern')
      
      // Commented - Python script auto-calculates these
      // formDataToSend.append('height', formData.height)
      // formDataToSend.append('width', formData.width)
      // formDataToSend.append('maxTileWidth', formData.maxTileWidth)
      
      // Keep - User can provide these manually
      if (formData.rowMultiple) {
        formDataToSend.append('rowMultiple', formData.rowMultiple)
      }
      if (formData.columnMultiple) {
        formDataToSend.append('columnMultiple', formData.columnMultiple)
      }

      await axiosInterceptorInstance.post('/patterns/upload', formDataToSend)

      CustomToast({
        message: 'Pattern uploaded successfully',
        type: 'success',
      })
      navigate('/pattern')
    } catch (error) {
      CustomToast({
        message: error.response?.data?.message || 'Failed to upload pattern',
        type: 'error',
      })
    } finally {
      setIsUploading(false)
    }
  }

  // Utility Functions
  const renderError = (field) =>
    errors[field] && (
      <p className='text-red-500 text-sm mt-1'>{errors[field]}</p>
    )

  const inputClass = (errorField) =>
    `w-full border ${
      errors[errorField] ? 'border-red-500' : 'border-gray-300'
    } rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-prime`

  // Component Render
  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={GoBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
              aria-label='Go back'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Add Pattern</h1>
          </div>
        </div>
      </PageHeader>

      <div className='flex flex-col items-center sm:gap-4'>
        {/* Row 1: Category and Memberships */}
        <div className='w-full sm:flex justify-center gap-6'>
          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Select Category:
            </label>
            <select
              className={inputClass('selectedCategory')}
              value={formData.selectedCategory}
              onChange={handleChange('selectedCategory')}
            >
              <option value=''>-- Select Category --</option>
              {categories.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {renderError('selectedCategory')}
          </div>

          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Select Memberships:
            </label>
            <div className='flex flex-wrap gap-4 mt-2'>
              {memberships.map((membership) => (
                <label key={membership} className='flex items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={formData.selectedMembership.includes(membership)}
                    onChange={handleMembershipChange(membership)}
                    className='w-4 h-4 text-prime focus:ring-prime rounded'
                  />
                  <span>{membership}</span>
                </label>
              ))}
            </div>
            {renderError('selectedMembership')}
          </div>
        </div>

        {/* Row 2: Pattern Name and Row Multiple */}
        <div className='w-full sm:flex justify-center gap-6'>
          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Pattern Name:
            </label>
            <input
              type='text'
              placeholder='Enter pattern name'
              className={inputClass('patternName')}
              value={formData.patternName}
              onChange={handleChange('patternName')}
            />
            {renderError('patternName')}
          </div>

          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Row Multiple:
            </label>
            <input
              type='number'
              placeholder='Enter row multiple (optional)'
              className={inputClass('rowMultiple')}
              value={formData.rowMultiple}
              onChange={handleChange('rowMultiple')}
              min='0'
              step='1'
            />
            {renderError('rowMultiple')}
            <p className='text-xs text-gray-500 mt-1'>
              Leave empty for auto-calculation
            </p>
          </div>
        </div>

        {/* Row 3: Column Multiple */}
        <div className='w-full sm:flex justify-center gap-6'>
          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Column Multiple:
            </label>
            <input
              type='number'
              placeholder='Enter column multiple (optional)'
              className={inputClass('columnMultiple')}
              value={formData.columnMultiple}
              onChange={handleChange('columnMultiple')}
              min='0'
              step='1'
            />
            {renderError('columnMultiple')}
            <p className='text-xs text-gray-500 mt-1'>
              Leave empty for auto-calculation
            </p>
          </div>

          {/* Empty div for alignment */}
          <div className='hidden sm:block w-full sm:w-[500px]'></div>
        </div>

        {/* Commented - width, height, maxTileWidth fields */}
        {/* 
        <div className='w-full flex flex-col sm:items-center gap-4'>
          <div className='w-full sm:flex justify-center gap-6'>
            {[
              { label: 'Pattern Width', field: 'width', type: 'number' },
            ].map(({ label, field, type }) => (
              <div key={field} className='w-full sm:w-[500px]'>
                <label className='block text-sm font-medium mt-2'>
                  {label}:
                </label>
                <input
                  type={type}
                  placeholder={label}
                  className={inputClass(field)}
                  value={formData[field]}
                  onChange={handleChange(field)}
                  min={type === 'number' ? 0 : undefined}
                />
                {renderError(field)}
              </div>
            ))}
          </div>

          <div className='w-full sm:flex justify-center gap-6'>
            {[
              { label: 'Pattern Height', field: 'height', type: 'number' },
              {
                label: 'Pattern Max Tile Width',
                field: 'maxTileWidth',
                type: 'number',
              },
            ].map(({ label, field, type }) => (
              <div key={field} className='w-full sm:w-[500px]'>
                <label className='block text-sm font-medium mt-2'>
                  {label}:
                </label>
                <input
                  type={type}
                  placeholder={label}
                  className={inputClass(field)}
                  value={formData[field]}
                  onChange={handleChange(field)}
                  min={type === 'number' ? 0 : undefined}
                />
                {renderError(field)}
              </div>
            ))}
          </div>
        </div>
        */}

        {/* File Upload */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mt-2'>
            Upload Pattern:
          </label>
          <div className='relative'>
            <div
              className={`relative w-full aspect-[9/5] rounded-lg cursor-pointer border ${
                errors.materialFile
                  ? 'border-red-500'
                  : 'border-dashed border-prime'
              } flex items-center justify-center`}
            >
              {!formData.materialPreview && (
                <input
                  type='file'
                  accept='.svg'
                  className='absolute inset-0 opacity-0 cursor-pointer'
                  ref={fileInputRef}
                  onChange={handleImageChange}
                />
              )}
              {formData.materialPreview ? (
                <img
                  src={formData.materialPreview}
                  alt='Preview'
                  className='absolute top-0 left-0 object-cover w-full h-full rounded-lg'
                />
              ) : (
                <div className='flex flex-col gap-4 items-center'>
                  <FiUpload className='text-4xl text-white bg-prime rounded-full p-2' />
                  <span className='text-prime'>
                    Only SVG files are accepted
                  </span>
                </div>
              )}

              {formData.materialFile && (
                <button
                  type='button'
                  onClick={handleDeleteImage}
                  className='absolute top-2 right-2 w-8 h-8 bg-white text-prime rounded-full flex items-center justify-center'
                >
                  <IoClose />
                </button>
              )}
            </div>
            {renderError('materialFile')}
          </div>
        </div>

        {/* Submit Button */}
        <button
          disabled={isUploading}
          onClick={handleSubmit}
          className='w-full sm:w-[500px] py-3 rounded-md text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-opacity-60 disabled:pointer-events-none'
        >
          {isUploading ? 'Uploading...' : 'Upload Pattern'}
        </button>
      </div>
    </div>
  )
}

export default PatternAdd